import { ref } from 'vue';
import { useModal } from '@/composables';

/**
 * Type of the event listener for custom events.
 */
export type CustomEventListener<K extends keyof CustomEventMap> = Parameters<
  typeof document.addEventListener<K>
>[1];

export function subscribeToCustomEvent<K extends keyof CustomEventMap>(
  eventName: K,
  listener: CustomEventListener<K>,
  target: EventTarget = document
) {
  target.addEventListener(eventName, listener);
}

export function unsubscribeFromCustomEvent<K extends keyof CustomEventMap>(
  eventName: K,
  listener: CustomEventListener<K>,
  target: EventTarget = document
) {
  target.removeEventListener(eventName, listener);
}

export function emitCustomEvent<K extends keyof CustomEventMap>(
  eventName: K,
  target: EventTarget
): void;
export function emitCustomEvent<K extends keyof CustomEventMap>(
  eventName: K,
  data: CustomEventMap[K]['detail'],
  target?: EventTarget
): void;
export function emitCustomEvent<K extends keyof CustomEventMap>(
  eventName: K,
  dataOrTarget?: CustomEventMap[K]['detail'] | EventTarget,
  maybeTarget?: EventTarget
) {
  const target = maybeTarget ?? (dataOrTarget instanceof EventTarget ? dataOrTarget : document);
  const data = dataOrTarget instanceof EventTarget ? undefined : dataOrTarget;

  const event = new CustomEvent(eventName, { detail: data });
  target.dispatchEvent(event);
}

/**
 * Composable for listening to dialog events (used in dialog components)
 *
 * @param dialogName - The name of the dialog to listen to
 * @returns Dialog listener instance with isDialogOpen, dialogProps, closeDialog, and subscription functions
 */
export function useDialogListener<K extends keyof CustomEventMap>(dialogName: K) {
  const { modal: isDialogOpen, openModal, closeModal } = useModal(false);
  const dialogProps = ref<CustomEventMap[K]['detail']>(null);

  const handleEvent = (event: CustomEvent<CustomEventMap[K]['detail']>) => {
    dialogProps.value = event.detail;
    openModal();
  };

  const closeDialog = () => {
    if (!isDialogOpen.value) {
      return;
    }

    closeModal();
  };

  const resetDialogProps = () => {
    dialogProps.value = null;
  };

  return {
    isDialogOpen,
    dialogProps,

    closeDialog,
    resetDialogProps,
    subscribeToDialogEvent: () => subscribeToCustomEvent(dialogName, handleEvent),
    unsubscribeFromDialogEvent: () => unsubscribeFromCustomEvent(dialogName, handleEvent)
  };
}

/**
 * Composable for triggering dialog events (used in components that open dialogs)
 *
 * @param dialogName - The name of the dialog to trigger
 * @returns Dialog trigger instance with triggerDialog function
 */
export function useDialogTrigger<K extends keyof CustomEventMap>(dialogName: K) {
  const triggerDialog = (newProps?: CustomEventMap[K]['detail']) => {
    emitCustomEvent(dialogName, newProps);
  };

  return {
    triggerDialog
  };
}

export type CustomEventMap = {
  /**
   * list here all the custom events that are used in the app
   * so the IDE's type inference helps us when emitting the events.
   */
  'open-remove-carrier-contact-dialog': CustomEvent<OpenRemoveCarrierContactDialogEvent>;
  'open-create-carrier-company-dialog': CustomEvent<OpenCreateCarrierCompanyDialogEvent>;
  'open-create-carrier-contact-dialog': CustomEvent<OpenCreateCarrierContactDialogEvent>;
  'open-create-yard-task-drawer': CustomEvent<OpenCreateYardTaskDialogEvent>;
  'open-gate-feed-image-carousel': CustomEvent<OpenGateFeedImageCarouselEvent>;
  'open-gate-feed-add-asset-dialog': CustomEvent<OpenGateFeedAddAssetDialogEvent>;
  'open-edit-yard-task-drawer': CustomEvent<OpenEditYardTaskDialogEvent>;
};

export type OpenRemoveCarrierContactDialogEvent = {
  carrierContact: {
    id: string;
    isFavorite: boolean;
    emailCCs: string[];
    company?: {
      name: string;
    };
    user: {
      company?: {
        name: string;
      };
      firstName?: string;
      lastName?: string;
      fullName?: string;
    };
  };
  mixpanelData?: unknown;
  onContactRemoved?: () => void;
};

export type OpenCreateCarrierCompanyDialogEvent = {
  mixpanelEntryPoint: string;
  onCompanyCreated?: (company: unknown) => void;
};

export type OpenCreateCarrierContactDialogEvent = {
  mixpanelEntryPoint: string;
  onContactCreated?: (contact: unknown) => void;
};

export type OpenCreateYardTaskDialogEvent = {
  warehouseId?: string;
  warehouseTimezone?: string;
  type?: 'move' | 'check';
  onTaskCreated?: (task: unknown) => void;
};

export type OpenEditYardTaskDialogEvent = {
  taskId: string;
  warehouseTimezone: string;
  onTaskUpdated?: (task: unknown) => void;
};

export type OpenGateFeedImageCarouselEvent = {
  images: string[];
};

export type OpenGateFeedAddAssetDialogEvent = {
  vehicleObservation: unknown;
};

declare global {
  interface DocumentEventMap extends CustomEventMap {}
}
